/**
 * ResizeElement  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef } from "react";
import { RESIZE_COMPONENT_TYPE, ResizeElementId, ResizeHandler } from "./ResizeTypes";
import { isFunction } from "@adapptio/lib-shared";
import { useResizeContext } from "./ResizeContext";
import { useForkRef } from "../../hooks/useForkRef";

export type TResizeElementProps<E extends React.ElementType = "div"> = {
	id: ResizeElementId;
	size: number | string;
	minSizePx?: number;
	resizeDisabled?: boolean;
	onResizeStart?: ResizeHandler;
	onResize?: ResizeHandler;
	onResizeStop?: ResizeHandler;
	Component?: E;
} & React.ComponentPropsWithRef<E>;

export const ResizeElement: React.FC<TResizeElementProps> = React.forwardRef(
	(
		{
			children,
			id: elementId,
			onResizeStart,
			onResize,
			onResizeStop,
			Component,
			resizeDisabled,
			size,
			minSizePx,
			...props
		},
		ref
	) => {
		const { registerElement, unregisterElement } = useResizeContext();
		const elRef = useRef<HTMLElement>();
		const forkRef = useForkRef(elRef, ref);

		useEffect(() => {
			if (isFunction(registerElement)) {
				registerElement(elementId, {
					id: elementId,
					type: RESIZE_COMPONENT_TYPE.ELEMENT,
					resizeDisabled,
					onResizeStart,
					onResize,
					onResizeStop,
					size,
					minSizePx,
					ref: elRef
				});
			}

			return () => {
				if (isFunction(unregisterElement)) {
					unregisterElement(elementId);
				}
			};
		}, [
			elementId,
			resizeDisabled,
			registerElement,
			unregisterElement,
			onResizeStart,
			onResize,
			onResizeStop,
			size,
			elRef,
			minSizePx
		]);

		return (
			<Component ref={forkRef} {...props}>
				{children}
			</Component>
		);
	}
);

ResizeElement.displayName = "ResizeElement";

ResizeElement.defaultProps = {
	Component: "div"
};
